import type { FeaturedBanner as FeaturedBannerType } from "@/types/database";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import "./featured-banner-container.css";
import {
  FEATURED_BANNER_COPY_CLASS,
  FEATURED_BANNER_DESCRIPTION_CLASS,
  FEATURED_BANNER_FRAME_CLASS,
  FEATURED_BANNER_ICON_CLASS,
  FEATURED_BANNER_ICON_IMAGE_CLASS,
} from "./featured-banner-description-classes";
import { normalizeFeaturedBannerTextColor } from "./featured-banner-text-color";

interface FeaturedBannerProps {
  banner: FeaturedBannerType;
  className?: string;
  /** Admin live preview: no link, overlay text visible immediately. */
  preview?: boolean;
}

export function FeaturedBanner({ banner, className = "", preview = false }: FeaturedBannerProps) {
  const [bgLoaded, setBgLoaded] = useState(false);
  const [iconLoaded, setIconLoaded] = useState(!banner.icon_url); // If no icon, consider it loaded
  const [iconFailed, setIconFailed] = useState(false);
  const bgImgRef = useRef<HTMLImageElement>(null);
  const iconImgRef = useRef<HTMLImageElement | null>(null);

  /** Reset when asset URLs change */
  useEffect(() => {
    setBgLoaded(false);
  }, [banner.image_url]);

  useEffect(() => {
    setIconLoaded(!banner.icon_url);
    setIconFailed(false);
  }, [banner.icon_url]);

  /**
   * Cached images often skip `onLoad` for a second `<img>` with the same `src` (e.g. Embla overlay
   * + carousel both mount FeaturedBanner). Sync from `complete` so overlay text always appears.
   */
  useEffect(() => {
    const el = bgImgRef.current;
    if (el?.complete && el.naturalWidth > 0) {
      setBgLoaded(true);
    }
  }, [banner.image_url]);

  useEffect(() => {
    if (!banner.icon_url) return;
    const el = iconImgRef.current;
    if (el?.complete && el.naturalWidth > 0) {
      setIconLoaded(true);
    }
  }, [banner.icon_url]);

  /** Fade in copy as soon as the hero image is ready — don’t wait on the icon (slow/broken CDN was hiding all text). */
  const overlayReady = preview || bgLoaded;
  const textColor = normalizeFeaturedBannerTextColor(banner.text_color);
  const wrapperClassName = `block ${className} group`;

  const frame = (
    <>
      <div className={FEATURED_BANNER_FRAME_CLASS} style={{ aspectRatio: "960/320" }}>
        {/* Background image skeleton */}
        <div
          className={`absolute inset-0 bg-brand-purple transition-opacity duration-300 ${bgLoaded ? "opacity-0" : "opacity-100"}`}
        >
          <div className="w-full h-full shimmer-gradient" />
        </div>

        {/* Background image */}
        {banner.image_url ? (
          <img
            ref={bgImgRef}
            src={banner.image_url}
            alt={banner.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onLoad={() => setBgLoaded(true)}
          />
        ) : null}

        {/* Content container */}
        <div
          className={`absolute inset-0 px-2 flex items-center transition-all duration-700 ${overlayReady ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
          style={{ transformOrigin: "center" }}
        >
          <div className={FEATURED_BANNER_COPY_CLASS} style={{ color: textColor }}>
            {/* Icon/Logo */}
            {banner.icon_url && !iconFailed && (
              <div
                className={`${FEATURED_BANNER_ICON_CLASS} transition-opacity duration-500 animate-float ${iconLoaded ? "opacity-100" : "opacity-0"}`}
              >
                <img
                  ref={iconImgRef}
                  src={banner.icon_url}
                  alt={`${banner.title} icon`}
                  className={FEATURED_BANNER_ICON_IMAGE_CLASS}
                  onLoad={() => setIconLoaded(true)}
                  onError={() => setIconFailed(true)}
                />
              </div>
            )}

            {/* Description */}
            {banner.description && (
              <p className={FEATURED_BANNER_DESCRIPTION_CLASS} style={{ color: textColor }}>
                {banner.description}
              </p>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .shimmer-gradient {
          position: relative;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, rgb(var(--brand-purple) / 0.9) 0%, rgb(var(--brand-indigo-soft) / 0.15) 50%, rgb(var(--brand-purple) / 0.9) 100%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite linear;
        }
        @keyframes shimmer {
          0% {
            background-position: -100% 0;
          }
          100% {
            background-position: 100% 0;
          }
        }
      `}</style>
    </>
  );

  if (preview) {
    return (
      <div className={wrapperClassName} data-testid="featured-banner-preview-frame">
        {frame}
      </div>
    );
  }

  return (
    <Link href={banner.destination_url} className={wrapperClassName}>
      {frame}
    </Link>
  );
}
